import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import AuthLayout from '../layout'
import routesPath from '@/utils/routesPath'
import { toast } from 'sonner'
import { useFormik } from 'formik'
import { resetPasswordSchema } from '@/validation-schemas/auth/login'
import { useResetPasswordMutation } from '@/redux/services/auth/authApi'
import type { Dictionary } from '@/types'
import { useEffect, useMemo, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'

const { LOGIN, FORGOT_PASSWORD } = routesPath

type ResetPayload = {
  email?: string
  code?: string
}

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const location = useLocation()

  const resetData = useMemo(() => {
    const stateData = location.state as ResetPayload | null
    if (stateData?.email && stateData?.code) return stateData

    try {
      const storedData = sessionStorage.getItem('reset-password-data')
      if (!storedData) return null
      const parsedData = JSON.parse(storedData) as ResetPayload
      return parsedData?.email && parsedData?.code ? parsedData : null
    } catch {
      return null
    }
  }, [location.state])

  const [resetPassword, { isLoading }] = useResetPasswordMutation()

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  useEffect(() => {
    if (!resetData?.email || !resetData?.code) {
      navigate(FORGOT_PASSWORD, { replace: true })
    }
  }, [navigate, resetData])

  const handleFormSubmit = async (values: Dictionary) => {
    try {
      const payload = {
        password: values.password,
        password_confirmation: values.confirmPassword,
        email: resetData?.email,
        code: resetData?.code,
      }
      const res = await resetPassword(payload).unwrap()
      toast.success(res?.message)
      sessionStorage.removeItem('reset-password-data')
      navigate(LOGIN)
    } catch (error: any) {
      toast.error(
        `${error?.data?.message ?? 'Failed to reset password. Please try again.'}`,
      )
    }
  }

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema: resetPasswordSchema,
    onSubmit: handleFormSubmit,
  })

  return (
    <AuthLayout
      title='Enter New Password!'
      subTitle='Please enter your new password'
      hasBack
    >
      <form onSubmit={formik.handleSubmit}>
        <div className='flex flex-col gap-3 '>
          <div className='relative items-center w-full'>
            <Input
              id='password'
              label='Password'
              name='password'
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.password}
              touched={formik.touched.password}
              error={formik.errors.password}
              type={showPassword ? 'text' : 'password'}
              placeholder='Enter your password at least 8 characters'
              className='pr-12 w-full'
            />
            <button
              type='button'
              className='absolute right-3 top-9.5 transform text-gray-500 hover:text-gray-700 transition-colors cursor-pointer'
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff className='h-4 w-4' />
              ) : (
                <Eye className='h-4 w-4' />
              )}
            </button>
          </div>

          <div className='relative'>
            <Input
              id='confirmPassword'
              label='Confirm Password'
              name='confirmPassword'
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.confirmPassword}
              touched={formik.touched.confirmPassword}
              error={formik.errors.confirmPassword}
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder='Enter your confirm password'
              className='pr-12'
            />
            <button
              type='button'
              className='absolute right-3 top-9.5 transform text-gray-500 hover:text-gray-700 transition-colors cursor-pointer'
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              tabIndex={-1}
            >
              {showConfirmPassword ? (
                <EyeOff className='h-4 w-4' />
              ) : (
                <Eye className='h-4 w-4' />
              )}
            </button>
          </div>
        </div>

        <div className='my-4 mt-10'>
          <Button
            type='submit'
            disabled={!(formik.isValid && formik.dirty) || isLoading}
            loading={isLoading}
          >
            Reset Password
          </Button>
          <span className='text-xs text-[#A3A3A3] block text-center pt-3'>
            Remember Password?{' '}
            <Link to={LOGIN} className='text-primary underline'>
              Login
            </Link>
          </span>
        </div>
      </form>
    </AuthLayout>
  )
}
